/**
 * IncidentReportForm Page
 *
 * Multi-step wizard form for technicians to report incidents in the field.
 * Step 1: Basic info (project, task, type)
 * Step 2: Details (title, severity, description)
 * Step 3: Evidence (photos, GPS)
 *
 * @module pages/technician/incidents/IncidentReportForm
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiTool,
  FiShield,
  FiPackage,
  FiClock,
  FiUsers,
  FiHelpCircle,
  FiCloud,
  FiMapPin,
  FiSend,
} from 'react-icons/fi';
import { createIncident, uploadIncidentPhoto } from '../../../services/incidentService';
import { supabase } from '../../../services/supabase';
import {
  INCIDENT_TYPE,
  INCIDENT_TYPE_LABELS,
  INCIDENT_SEVERITY,
  PROJECT_STATUS,
} from '../../../utils/constants';
import SeveritySelector from './SeveritySelector';
import PhotoCapture from './PhotoCapture';
import './IncidentReportForm.css';

const TYPE_CONFIG = [
  {
    value: INCIDENT_TYPE.EQUIPMENT_FAILURE,
    label: INCIDENT_TYPE_LABELS[INCIDENT_TYPE.EQUIPMENT_FAILURE],
    icon: FiTool,
    description: 'Falla o averia de equipo',
  },
  {
    value: INCIDENT_TYPE.SAFETY,
    label: INCIDENT_TYPE_LABELS[INCIDENT_TYPE.SAFETY],
    icon: FiShield,
    description: 'Riesgo o accidente de seguridad',
  },
  {
    value: INCIDENT_TYPE.MATERIAL_ISSUE,
    label: INCIDENT_TYPE_LABELS[INCIDENT_TYPE.MATERIAL_ISSUE],
    icon: FiPackage,
    description: 'Material faltante o danado',
  },
  {
    value: INCIDENT_TYPE.DELAY,
    label: INCIDENT_TYPE_LABELS[INCIDENT_TYPE.DELAY],
    icon: FiClock,
    description: 'Retraso en el proyecto',
  },
  {
    value: INCIDENT_TYPE.CLIENT_ISSUE,
    label: INCIDENT_TYPE_LABELS[INCIDENT_TYPE.CLIENT_ISSUE],
    icon: FiUsers,
    description: 'Problema con el cliente',
  },
  {
    value: INCIDENT_TYPE.WEATHER,
    label: INCIDENT_TYPE_LABELS[INCIDENT_TYPE.WEATHER],
    icon: FiCloud,
    description: 'Condicion climatica adversa',
  },
  {
    value: INCIDENT_TYPE.OTHER,
    label: INCIDENT_TYPE_LABELS[INCIDENT_TYPE.OTHER],
    icon: FiHelpCircle,
    description: 'Otro tipo de incidente',
  },
];

const STEPS = [
  { id: 1, title: 'Información básica', subtitle: 'Proyecto y tipo' },
  { id: 2, title: 'Detalles', subtitle: 'Título y severidad' },
  { id: 3, title: 'Evidencia', subtitle: 'Fotos y ubicación' },
];

const IncidentReportForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Form state
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    project_id: searchParams.get('project') || '',
    task_id: searchParams.get('task') || '',
    incident_type: '',
    title: '',
    severity: INCIDENT_SEVERITY.MEDIUM,
    description: '',
    work_stopped: false,
  });

  // Photo state
  const [photos, setPhotos] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});

  // Location state
  const [includeLocation, setIncludeLocation] = useState(true);
  const [location, setLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState(null);

  // Data loading state
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingTasks, setLoadingTasks] = useState(false);

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Load projects assigned to technician
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        // Get projects where user is assigned as a team member or has tasks
        const { data, error } = await supabase
          .from('projects')
          .select('id, project_number, title, status')
          // Issue #296: `'active'` is not a value `projects.status` ever takes
          // (the live set is quotation_sent / quotation_accepted / po_received /
          // in_progress / closed). Filtering on it returned an empty project
          // list, step 1 could never validate, and "Siguiente" stayed dead.
          .in('status', [PROJECT_STATUS.IN_PROGRESS, PROJECT_STATUS.PO_RECEIVED])
          .order('updated_at', { ascending: false });

        if (error) throw error;
        setProjects(data || []);
      } catch (error) {
        console.error('[INCIDENT] Error loading projects:', error);
      } finally {
        setLoadingProjects(false);
      }
    };

    fetchProjects();
  }, []);

  // Load tasks when project is selected
  useEffect(() => {
    if (!formData.project_id) {
      setTasks([]);
      return;
    }

    const fetchTasks = async () => {
      setLoadingTasks(true);
      try {
        const { data, error } = await supabase
          .from('tasks')
          .select('id, task_number, title, status')
          .eq('project_id', formData.project_id)
          .in('status', ['pending', 'in_progress', 'blocked'])
          .order('created_at', { ascending: false });

        if (error) throw error;
        setTasks(data || []);
      } catch (error) {
        console.error('[INCIDENT] Error loading tasks:', error);
      } finally {
        setLoadingTasks(false);
      }
    };

    fetchTasks();
  }, [formData.project_id]);

  // Get current location
  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError('Geolocalizacion no soportada en este dispositivo');
      return;
    }

    setLocationLoading(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
        setLocationLoading(false);
      },
      (error) => {
        let errorMessage = 'Error al obtener ubicacion';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Permiso de ubicacion denegado';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Ubicacion no disponible';
            break;
          case error.TIMEOUT:
            errorMessage = 'Tiempo de espera agotado';
            break;
          default:
            break;
        }
        setLocationError(errorMessage);
        setLocationLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  }, []);

  // Get location when entering step 3
  useEffect(() => {
    if (currentStep === 3 && includeLocation && !location && !locationLoading) {
      getCurrentLocation();
    }
  }, [currentStep, includeLocation, location, locationLoading, getCurrentLocation]);

  // Form handlers
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Reset task when project changes
    if (field === 'project_id') {
      setFormData((prev) => ({ ...prev, task_id: '' }));
    }
  };

  const handleTypeSelect = (type) => {
    setFormData((prev) => ({ ...prev, incident_type: type }));
  };

  // Validation
  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.project_id && formData.incident_type;
      case 2:
        return formData.title.trim() && formData.severity && formData.description.trim();
      case 3:
        return true; // Photos and location are optional
      default:
        return false;
    }
  };

  // Navigation
  const handleNext = () => {
    if (validateStep(currentStep) && currentStep < STEPS.length) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  // Submit form
  const handleSubmit = async () => {
    if (!validateStep(1) || !validateStep(2)) {
      setSubmitError('Por favor complete todos los campos requeridos');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      // Upload photos first
      const uploadedUrls = [];

      if (photos.length > 0) {
        for (let i = 0; i < photos.length; i++) {
          const photo = photos[i];

          if (!photo.uploaded && photo.file) {
            setUploadProgress((prev) => ({ ...prev, [i]: 0 }));

            // Generate temporary ID for upload
            const tempId = `temp_${Date.now()}_${i}`;
            const { url, error } = await uploadIncidentPhoto(photo.file, tempId);

            if (error) {
              console.error('[INCIDENT] Photo upload error:', error);
              throw new Error(`Error subiendo foto ${i + 1}`);
            }

            uploadedUrls.push(url);
            setUploadProgress((prev) => ({ ...prev, [i]: 100 }));
          } else if (photo.url) {
            uploadedUrls.push(photo.url);
          }
        }
      }

      // Create incident
      const incidentData = {
        project_id: formData.project_id,
        task_id: formData.task_id || null,
        incident_type: formData.incident_type,
        severity: formData.severity,
        title: formData.title.trim(),
        description: formData.description.trim(),
        work_stopped: formData.work_stopped,
        photos: uploadedUrls,
        // `incidents` stores the fix as three flat numeric columns
        // (latitude / longitude / location_accuracy) and `createIncident` reads
        // exactly those keys. This used to send a nested `location` object,
        // which the service never looked at: the step-3 UI said "Ubicacion
        // capturada" and the row landed with NULL coordinates (9 of 9 rows in
        // prod had latitude NULL).
        ...(includeLocation && location
          ? {
              latitude: location.latitude,
              longitude: location.longitude,
              location_accuracy: location.accuracy,
            }
          : {}),
      };

      const { data, error } = await createIncident(incidentData);

      if (error) throw error;

      setSubmitSuccess(true);

      // Redirect after brief success message
      setTimeout(() => {
        navigate('/tecnico/incidentes', {
          state: { newIncident: data },
        });
      }, 2000);
    } catch (error) {
      console.error('[INCIDENT] Submit error:', error);
      setSubmitError(error.message || 'Error al enviar el reporte');
    } finally {
      setSubmitting(false);
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="step-content">
            {/* Project selector */}
            <Form.Group className="mb-4">
              <Form.Label className="form-label-required">Proyecto</Form.Label>
              <Form.Select
                value={formData.project_id}
                onChange={(e) => handleInputChange('project_id', e.target.value)}
                disabled={loadingProjects}
                className="form-select-lg"
              >
                <option value="">
                  {loadingProjects ? 'Cargando proyectos...' : 'Seleccionar proyecto'}
                </option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.project_number} - {project.title}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Task selector (optional) */}
            <Form.Group className="mb-4">
              <Form.Label>Tarea relacionada (opcional)</Form.Label>
              <Form.Select
                value={formData.task_id}
                onChange={(e) => handleInputChange('task_id', e.target.value)}
                disabled={!formData.project_id || loadingTasks}
                className="form-select-lg"
              >
                <option value="">
                  {loadingTasks
                    ? 'Cargando tareas...'
                    : !formData.project_id
                    ? 'Seleccione un proyecto primero'
                    : 'Ninguna tarea especifica'}
                </option>
                {tasks.map((task) => (
                  <option key={task.id} value={task.id}>
                    #{task.task_number} - {task.title}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Incident type */}
            <Form.Group className="mb-3">
              <Form.Label className="form-label-required">Tipo de incidente</Form.Label>
              <div className="incident-type-grid">
                {TYPE_CONFIG.map((type) => {
                  const Icon = type.icon;
                  const isSelected = formData.incident_type === type.value;

                  return (
                    <button
                      key={type.value}
                      type="button"
                      className={`incident-type-btn ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleTypeSelect(type.value)}
                    >
                      <div className="incident-type-icon">
                        <Icon size={24} />
                      </div>
                      <div className="incident-type-info">
                        <span className="incident-type-label">{type.label}</span>
                        <span className="incident-type-desc">{type.description}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </Form.Group>
          </div>
        );

      case 2:
        return (
          <div className="step-content">
            {/* Title */}
            <Form.Group className="mb-4">
              <Form.Label className="form-label-required">Titulo del incidente</Form.Label>
              <Form.Control
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Describa brevemente el incidente"
                maxLength={150}
                className="form-control-lg"
              />
              <Form.Text className="text-muted">
                {formData.title.length}/150 caracteres
              </Form.Text>
            </Form.Group>

            {/* Severity */}
            <Form.Group className="mb-4">
              <Form.Label className="form-label-required">Severidad</Form.Label>
              <SeveritySelector
                value={formData.severity}
                onChange={(value) => handleInputChange('severity', value)}
              />
            </Form.Group>

            {/* Description */}
            <Form.Group className="mb-4">
              <Form.Label className="form-label-required">Descripcion detallada</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Describe lo ocurrido con el mayor detalle posible: que paso, cuando, donde, quien estaba presente, etc."
                maxLength={2000}
                className="form-textarea-lg"
              />
              <Form.Text className="text-muted">
                {formData.description.length}/2000 caracteres
              </Form.Text>
            </Form.Group>

            {/* Work stopped checkbox */}
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="work-stopped"
                label="El trabajo fue detenido debido a este incidente"
                checked={formData.work_stopped}
                onChange={(e) => handleInputChange('work_stopped', e.target.checked)}
                className="work-stopped-switch"
              />
            </Form.Group>
          </div>
        );

      case 3:
        return (
          <div className="step-content">
            {/* Photo capture */}
            <Form.Group className="mb-4">
              <Form.Label>Evidencia fotografica (hasta 5 fotos)</Form.Label>
              <PhotoCapture
                photos={photos}
                onPhotosChange={setPhotos}
                maxPhotos={5}
                disabled={submitting}
                uploading={submitting}
                uploadProgress={uploadProgress}
              />
            </Form.Group>

            {/* Location */}
            <Form.Group className="mb-4">
              <Form.Label>Ubicacion GPS</Form.Label>

              <Form.Check
                type="switch"
                id="include-location"
                label="Incluir ubicacion actual"
                checked={includeLocation}
                onChange={(e) => setIncludeLocation(e.target.checked)}
                className="mb-3"
              />

              {includeLocation && (
                <div className="location-status">
                  {locationLoading ? (
                    <div className="location-loading">
                      <Spinner animation="border" size="sm" />
                      <span>Obteniendo ubicacion...</span>
                    </div>
                  ) : locationError ? (
                    <div className="location-error">
                      <FiMapPin className="text-danger" />
                      <span>{locationError}</span>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={getCurrentLocation}
                        className="p-0 ms-2"
                      >
                        Reintentar
                      </Button>
                    </div>
                  ) : location ? (
                    <div className="location-success">
                      <FiMapPin className="text-success" />
                      <span>
                        Ubicacion capturada
                        <small className="d-block text-muted">
                          Precision: ~{Math.round(location.accuracy)}m
                        </small>
                      </span>
                    </div>
                  ) : (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={getCurrentLocation}
                    >
                      <FiMapPin className="me-2" />
                      Obtener ubicacion
                    </Button>
                  )}
                </div>
              )}
            </Form.Group>

            {/* Summary */}
            <Card className="summary-card">
              <Card.Body>
                <h6 className="summary-title">Resumen del reporte</h6>
                <div className="summary-item">
                  <span className="summary-label">Proyecto:</span>
                  <span className="summary-value">
                    {projects.find((p) => p.id === formData.project_id)?.project_number ||
                      'No seleccionado'}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Tipo:</span>
                  <span className="summary-value">
                    {INCIDENT_TYPE_LABELS[formData.incident_type] || 'No seleccionado'}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Titulo:</span>
                  <span className="summary-value">
                    {formData.title || 'No especificado'}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Fotos:</span>
                  <span className="summary-value">{photos.length} adjunta(s)</span>
                </div>
              </Card.Body>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  // Success screen
  if (submitSuccess) {
    return (
      <Container fluid className="technician-page">
        <div className="success-screen">
          <div className="success-icon">
            <FiCheck size={64} />
          </div>
          <h2>Reporte Enviado</h2>
          <p>El incidente ha sido reportado exitosamente.</p>
          <p className="text-muted">Redirigiendo...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="technician-page incident-form-page">
      {/* Header */}
      <div className="page-header">
        <Link to="/tecnico/incidentes" className="back-link">
          <FiArrowLeft /> Volver
        </Link>
        <h1 className="page-title">Reportar Incidente</h1>
        <p className="page-subtitle">Paso {currentStep} de {STEPS.length}</p>
      </div>

      {/* Progress steps */}
      <div className="wizard-progress">
        {STEPS.map((step, index) => (
          <div
            key={step.id}
            className={`wizard-step ${
              currentStep === step.id
                ? 'active'
                : currentStep > step.id
                ? 'completed'
                : ''
            }`}
          >
            <div className="wizard-step-indicator">
              {currentStep > step.id ? <FiCheck size={14} /> : step.id}
            </div>
            <div className="wizard-step-info">
              <span className="wizard-step-title">{step.title}</span>
              <span className="wizard-step-subtitle">{step.subtitle}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Form card */}
      <Card className="form-card">
        <Card.Body>
          {submitError && (
            <Alert variant="danger" className="mb-3" dismissible onClose={() => setSubmitError(null)}>
              {submitError}
            </Alert>
          )}

          {renderStepContent()}
        </Card.Body>
      </Card>

      {/* Navigation buttons */}
      <div className="wizard-nav">
        {currentStep > 1 && (
          <Button
            variant="outline-secondary"
            onClick={handleBack}
            disabled={submitting}
            className="wizard-btn back"
          >
            <FiArrowLeft /> Atras
          </Button>
        )}

        {currentStep < STEPS.length ? (
          <Button
            variant="primary"
            onClick={handleNext}
            disabled={!validateStep(currentStep)}
            className="wizard-btn next"
          >
            Siguiente <FiArrowRight />
          </Button>
        ) : (
          <Button
            variant="danger"
            onClick={handleSubmit}
            disabled={submitting || !validateStep(1) || !validateStep(2)}
            className="wizard-btn submit"
          >
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Enviando...
              </>
            ) : (
              <>
                <FiSend /> Enviar Reporte
              </>
            )}
          </Button>
        )}
      </div>
    </Container>
  );
};

export default IncidentReportForm;
